import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Clock, Calendar, Plus, X, Send, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { toast } from 'sonner';

export const SensoryDailyReportAdminPage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';
  
  const [settings, setSettings] = useState({
    is_enabled: false,
    recipient_emails: [],
    send_hour: 18,
    send_minute: 0,
    report_type: 'today'
  });
  
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [sensoryType]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sensory/daily-report-settings', { params: { sensory_type: sensoryType } });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/sensory/daily-report-settings', settings, { params: { sensory_type: sensoryType } });
      toast.success('Nastavení uloženo');
    } catch (error) {
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    
    // Basic email validation
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Neplatný formát emailu');
      return;
    }
    
    if (settings.recipient_emails.includes(email)) {
      toast.error('Email již je v seznamu');
      return;
    }
    
    setSettings({
      ...settings,
      recipient_emails: [...settings.recipient_emails, email]
    });
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setSettings({
      ...settings,
      recipient_emails: settings.recipient_emails.filter(e => e !== email)
    });
  };

  const handleTestSend = async () => {
    if (settings.recipient_emails.length === 0) {
      toast.error('Nejprve přidejte alespoň jeden email');
      return;
    }
    
    setTesting(true);
    try {
      // First save settings
      await api.put('/sensory/daily-report-settings', {
        ...settings,
        is_enabled: true // Temporarily enable for test
      }, { params: { sensory_type: sensoryType } });
      
      // Then send test
      await api.post('/sensory/daily-report-settings/test', null, { params: { sensory_type: sensoryType } });
      toast.success(`Testovací email (${typeLabel}) odeslán`);
    } catch (error) {
      toast.error('Chyba při odesílání testu');
    } finally {
      setTesting(false);
    }
  };

  // Generate hour options
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = [0, 15, 30, 45];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Denní report - {typeLabel}</h1>
            <p className="text-slate-500">Nastavení emailového souhrnu pro Senzorika {typeLabel}</p>
          </div>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Enable/Disable */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Mail className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Automatické odesílání</h3>
                  <p className="text-sm text-slate-500">Povolit denní souhrn pro {typeLabel}</p>
                </div>
              </div>
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
                data-testid="enable-toggle"
              />
            </div>
          </div>

          {/* Time Settings */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-slate-400" />
              <h3 className="font-semibold text-slate-800">Čas odesílání</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hodina</Label>
                <Select
                  value={String(settings.send_hour)}
                  onValueChange={(val) => setSettings({ ...settings, send_hour: parseInt(val) })}
                >
                  <SelectTrigger data-testid="hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hourOptions.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Minuta</Label>
                <Select
                  value={String(settings.send_minute)}
                  onValueChange={(val) => setSettings({ ...settings, send_minute: parseInt(val) })}
                >
                  <SelectTrigger data-testid="minute-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minuteOptions.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        :{String(m).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="pt-4 border-t border-slate-100">
              <Label>Reportovat za</Label>
              <Select
                value={settings.report_type}
                onValueChange={(val) => setSettings({ ...settings, report_type: val })}
              >
                <SelectTrigger data-testid="report-type-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Dnešní den
                    </div>
                  </SelectItem>
                  <SelectItem value="yesterday">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Předchozí den
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                {settings.report_type === 'today' 
                  ? 'Report bude obsahovat zkoušky z aktuálního dne'
                  : 'Report bude obsahovat zkoušky z předchozího dne (vhodné pro ranní odesílání)'
                }
              </p>
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-slate-400" />
              <h3 className="font-semibold text-slate-800">Příjemci</h3>
            </div>
            
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                data-testid="email-input"
              />
              <Button onClick={handleAddEmail} data-testid="add-email-btn">
                <Plus className="h-4 w-4 mr-1" />
                Přidat
              </Button>
            </div>
            
            {settings.recipient_emails.length > 0 ? (
              <div className="space-y-2 mt-4">
                {settings.recipient_emails.map((email) => (
                  <div 
                    key={email} 
                    className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2"
                  >
                    <span className="text-slate-700">{email}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleRemoveEmail(email)}
                      data-testid={`remove-email-${email}`}
                    >
                      <X className="h-4 w-4 text-slate-400 hover:text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                Zatím nejsou přidáni žádní příjemci
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={handleTestSend}
              disabled={testing || settings.recipient_emails.length === 0}
              data-testid="test-send-btn"
            >
              {testing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500 mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Odeslat testovací email
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-violet-500 to-purple-500"
              data-testid="save-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Uložit nastavení
            </Button>
          </div>

          {/* Info */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <h4 className="font-medium text-violet-800 mb-2">💡 Jak to funguje</h4>
            <ul className="text-sm text-violet-700 space-y-1">
              <li>• Report obsahuje souhrn všech senzorických zkoušek za vybraný den</li>
              <li>• U každého jídla jsou zobrazena hodnocení A/B/C a poznámky hodnotitelů</li>
              <li>• Email se odesílá automaticky v nastavený čas každý den</li>
              <li>• Pokud nejsou v daný den žádné zkoušky, email se neodešle</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SensoryDailyReportAdminPage;
